/**
 * Date helpers local to the Sales Reports page.
 *
 * The Indian financial year (April - March) convention mirrors erp-api's
 * resolveRange() in controllers/salesAnalytics.js — the backend already
 * defaults every /sales-analytics/* endpoint to "current FY to date" when
 * from/to are omitted, so the UI's default must match or the first paint
 * (no explicit dates) and a later explicit fetch would silently disagree.
 */

const toISO = (d: Date): string => d.toISOString().split('T')[0];

/** Current Indian financial year, from 1 April to today. */
export const getCurrentFYRange = (): { from: string; to: string } => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + 1; // 1-12
  const fyStartYear = month >= 4 ? year : year - 1;
  return { from: `${fyStartYear}-04-01`, to: toISO(today) };
};

/** Whole-day span between two YYYY-MM-DD dates, inclusive. */
const spanDays = (from: string, to: string): number => {
  const a = new Date(`${from}T00:00:00`);
  const b = new Date(`${to}T00:00:00`);
  return Math.round((b.getTime() - a.getTime()) / 86400000) + 1;
};

/** Immediately preceding period of equal length, ending the day before `from`. */
export const getDefaultComparePeriod = (from: string, to: string): { from: string; to: string } => {
  const length = Math.max(spanDays(from, to), 1);
  const fromDate = new Date(`${from}T00:00:00`);
  const compareTo = new Date(fromDate);
  compareTo.setDate(compareTo.getDate() - 1);
  const compareFrom = new Date(compareTo);
  compareFrom.setDate(compareFrom.getDate() - (length - 1));
  return { from: toISO(compareFrom), to: toISO(compareTo) };
};

export const formatDateIN = (value: string | null | undefined): string => {
  if (!value) return '—';
  const d = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const formatDateShortIN = (value: string | null | undefined): string => {
  if (!value) return '—';
  const d = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

/** Whole days between an ISO date and today; positive = in the past. */
export const daysSince = (isoDate: string | null | undefined): number | null => {
  if (!isoDate) return null;
  const then = new Date(`${isoDate.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(then.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((today.getTime() - then.getTime()) / 86400000);
};

/** Whole days between today and a future ISO date; positive = still in the future. */
export const daysUntil = (isoDate: string | null | undefined): number | null => {
  const since = daysSince(isoDate);
  return since === null ? null : -since;
};

export const FUNNEL_STEP_LABELS: Record<string, string> = {
  leads: 'Leads',
  qualified: 'Qualified',
  opportunities: 'Opportunities',
  quotations: 'Quotations',
  sales_orders: 'Sales Orders',
  invoices: 'Invoices',
};

export const PIPELINE_STAGE_LABELS: Record<string, string> = {
  PROSPECTING: 'Prospecting',
  QUALIFICATION: 'Qualification',
  PROPOSAL: 'Proposal',
  NEGOTIATION: 'Negotiation',
  WON: 'Won',
  LOST: 'Lost',
};
